// src/components/Tickets/CreateTicketWizard.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../hooks/useToast';
import { ticketService } from '../../services/ticketService';
import { aiAnalysisService } from '../../services/aiAnalysisService';
import Toast from '../common/Toast';
import {
  ArrowLeft, ArrowRight, Check, AlertCircle, Wrench,
  FileText, Eye, ChevronDown, Sparkles, X, Lightbulb,
  ExternalLink, Loader, MessageSquare, Upload, Image, Paperclip  // AGREGAR estos 3
} from 'lucide-react';

interface TicketFormData {
  title: string;
  category: string;
  priority: string;
  description: string;
  motor: string;
  configuration: string;
  component: string;
  partNumber: string;
  additionalComments: string;
}

interface AISuggestion {
  type: 'similar_ticket' | 'knowledge_article' | 'quick_tip';
  title: string;
  content: string;
  relevance: number;
  ticketId?: string;
  articleId?: string;
}

const STEPS = [
  { id: 1, name: 'basic', icon: FileText },
  { id: 2, name: 'details', icon: AlertCircle },
  { id: 3, name: 'technical', icon: Wrench },
  { id: 4, name: 'review', icon: Eye }
];

export default function CreateTicketWizard() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { toasts, removeToast, success, error: showError, info } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestion[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreview, setFilePreview] = useState<{ [key: string]: string }>({}); 
  const [formData, setFormData] = useState<TicketFormData>({
    title: '',
    category: '',
    priority: '',
    description: '',
    motor: '',
    configuration: '',
    component: '',
    partNumber: '',
    additionalComments: ''
  });

  const updateField = (field: keyof TicketFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const motor = params.get('motor');
  const config = params.get('configuration');
  
  if (motor || config) {
    // Pre-llenar formulario
    setFormData(prev => ({
      ...prev,
      motor: motor || '',
      configuration: config || ''
    }));
  }
}, []);
  // Análisis IA cuando cambia la descripción
useEffect(() => {
  const analyzeWithAllFields = async () => {
    // Solo analizar en el paso 3 (información técnica)
    if (currentStep !== 3) {
      setAiSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Construir descripción completa con TODOS los campos
    let searchQuery = '';
    
    // Prioridad: título
    if (formData.title) {
      searchQuery += `Título: ${formData.title}. `;
    }
    
    // Descripción completa
    if (formData.description) {
      searchQuery += `Descripción: ${formData.description}. `;
    }
    
    // Información técnica específica
    if (formData.motor) {
      searchQuery += `Motor: ${formData.motor}. `;
    }
    
    if (formData.configuration) {
      searchQuery += `Configuración: ${formData.configuration}. `;
    }
    
    if (formData.component) {
      searchQuery += `Componente: ${formData.component}. `;
    }
    
    if (formData.partNumber) {
      searchQuery += `Número de parte: ${formData.partNumber}. `;
    }

    // Necesita al menos 30 caracteres para analizar
    if (searchQuery.length < 30) {
      setAiSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsAnalyzing(true);
    try {
      console.log('🔍 [IA] Query completa:', searchQuery.substring(0, 100) + '...');
      
      const suggestions = await aiAnalysisService.analyzeDescription(searchQuery);
      setAiSuggestions(suggestions);
      
      if (suggestions.length > 0) {
        setShowSuggestions(true);
        // No mostrar toast en cada cambio, solo la primera vez
        if (aiSuggestions.length === 0) {
          info(`Encontradas ${suggestions.length} sugerencias relevantes`);
        }
      } else {
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('❌ [IA] Error analizando:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Debounce de 1 segundo para no saturar el servidor
  const timeoutId = setTimeout(analyzeWithAllFields, 1000);
  return () => clearTimeout(timeoutId);
}, [
  currentStep, 
  formData.title,
  formData.description, 
  formData.motor, 
  formData.configuration, 
  formData.component, 
  formData.partNumber
]);

  const validateStep = (step: number): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (step === 1) {
      if (!formData.title.trim()) {
        newErrors.title = t('tickets.wizard.validation.titleRequired');
      } else if (formData.title.length < 10) {
        newErrors.title = t('tickets.wizard.validation.titleMin');
      }
      if (!formData.category) {
        newErrors.category = t('tickets.wizard.validation.categoryRequired');
      }
      if (!formData.priority) {
        newErrors.priority = t('tickets.wizard.validation.priorityRequired');
      }
    }

    if (step === 2) {
      if (!formData.description.trim()) {
        newErrors.description = t('tickets.wizard.validation.descriptionRequired');
      } else if (formData.description.length < 20) {
        newErrors.description = t('tickets.wizard.validation.descriptionMin');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

const handleSubmit = async () => {
  if (!validateStep(2)) return;

  try {
    setIsSubmitting(true);
    console.log('📤 Enviando ticket con', selectedFiles.length, 'archivos...');
    
    // ✅ CREAR FormData CORRECTAMENTE
    const formDataToSend = new FormData();
    
    // Agregar campos del ticket
    formDataToSend.append('title', formData.title);
    formDataToSend.append('category', formData.category);
    formDataToSend.append('priority', formData.priority);
    formDataToSend.append('description', formData.description);
    
    if (formData.motor) formDataToSend.append('motor', formData.motor);
    if (formData.configuration) formDataToSend.append('configuration', formData.configuration);
    if (formData.component) formDataToSend.append('component', formData.component);
    if (formData.partNumber) formDataToSend.append('part_number', formData.partNumber);
    if (formData.additionalComments) formDataToSend.append('additional_comments', formData.additionalComments);
    
    formDataToSend.append('user_email', user!.email);
    formDataToSend.append('user_name', user!.name);
    formDataToSend.append('status', 'open');
    
    // ✅ Agregar archivos
    selectedFiles.forEach((file) => {
      formDataToSend.append('attachments', file);
    });
    
    console.log('📎 Total archivos a enviar:', selectedFiles.length);
    
    const response = await ticketService.createTicket(formDataToSend);
    
    console.log('✅ Ticket creado:', response.ticket.id);
    success(t('tickets.wizard.success.created'));
    
    setTimeout(() => {
      navigate(`/dashboard/tickets/${response.ticket.id}`);
    }, 1500);
  } catch (err) {
    console.error('❌ Error creando ticket:', err);
    showError('Error al crear el ticket. Intenta nuevamente.');
  } finally {
    setIsSubmitting(false);
  }
};

const renderStepIndicator = () => {
  const stepConfig = [
    { id: 1, name: 'basic', icon: FileText, label: t('tickets.wizard.steps.basic') },
    { id: 2, name: 'details', icon: AlertCircle, label: t('tickets.wizard.steps.details') },
    { id: 3, name: 'technical', icon: Wrench, label: t('tickets.wizard.steps.technical') },
    { id: 4, name: 'review', icon: Eye, label: t('tickets.wizard.steps.review') }
  ];

  return (
    <div className="mb-12">
      {/* Barra de progreso superior */}
      <div className="relative mb-8">
        <div className={`absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 ${
          isDark ? 'bg-gray-800' : 'bg-gray-200'
        }`} />
        <div 
          className="absolute top-1/2 left-0 h-1 -translate-y-1/2 bg-blue-500 transition-all duration-500"
          style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
        />
        
        {/* Steps */}
        <div className="relative flex justify-between">
          {stepConfig.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            const isPending = currentStep < step.id;

            return (
              <div key={step.id} className="flex flex-col items-center" style={{ width: '120px' }}>
                {/* Número de paso */}
                <div className="relative mb-3">
                  <div
                    className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      isCompleted
                        ? 'bg-green-500 shadow-lg shadow-green-500/30'
                        : isActive
                        ? 'bg-blue-500 shadow-xl shadow-blue-500/40 scale-110'
                        : isDark
                        ? 'bg-gray-800 border-2 border-gray-700'
                        : 'bg-white border-2 border-gray-300 shadow-sm'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="w-10 h-10 text-white" strokeWidth={3} />
                    ) : (
                      <Icon className={`w-9 h-9 ${
                        isActive 
                          ? 'text-white' 
                          : isDark 
                            ? 'text-gray-500' 
                            : 'text-gray-400'
                      }`} />
                    )}
                  </div>
                  
                  {/* Número pequeño */}
                  <div
                    className={`absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isCompleted
                        ? 'bg-green-600 text-white'
                        : isActive
                        ? 'bg-blue-600 text-white'
                        : isDark
                        ? 'bg-gray-700 text-gray-400'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step.id}
                  </div>
                </div>

                {/* Texto del paso */}
                <div className="text-center">
                  <p
                    className={`text-xs font-semibold uppercase tracking-wider mb-1 ${
                      isActive
                        ? 'text-blue-500'
                        : isCompleted
                        ? 'text-green-500'
                        : isDark
                        ? 'text-gray-500'
                        : 'text-gray-400'
                    }`}
                  >
                    {step.label}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

  const renderAISuggestions = () => {
    if (!showSuggestions || aiSuggestions.length === 0) return null;

    return (
      <div className={`mt-6 ${isDark ? 'bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-purple-500/30' : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200'} border rounded-xl p-4`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Sugerencias de IA
            </h4>
            {isAnalyzing && <Loader className="w-4 h-4 animate-spin text-purple-500" />}
          </div>
          <button
            onClick={() => setShowSuggestions(false)}
            className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          {aiSuggestions.map((suggestion, index) => {
            const Icon = suggestion.type === 'quick_tip' ? Lightbulb : 
                        suggestion.type === 'similar_ticket' ? FileText : 
                        ExternalLink;

            return (
              <div
                key={index}
                className={`${isDark ? 'bg-gray-800/50 hover:bg-gray-800' : 'bg-white hover:bg-gray-50'} p-3 rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'} transition-all cursor-pointer`}
                onClick={() => {
                  if (suggestion.ticketId) {
                    navigate(`/dashboard/tickets/${suggestion.ticketId}`);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <Icon className={`w-5 h-5 flex-shrink-0 ${
                    suggestion.type === 'quick_tip' ? 'text-yellow-500' :
                    suggestion.type === 'similar_ticket' ? 'text-blue-500' :
                    'text-green-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>
                      {suggestion.title}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {suggestion.content}
                    </p>
                  </div>
                  {suggestion.ticketId && (
                    <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

const renderStep2 = () => (
  <div className="space-y-6">
    <div>
      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
        {t('tickets.wizard.fields.description')} <span className="text-red-500">*</span>
      </label>
      <textarea
        value={formData.description}
        onChange={(e) => updateField('description', e.target.value)}
        placeholder={t('tickets.wizard.fields.descriptionPlaceholder')}
        rows={10}
        className={`w-full px-4 py-3 ${
          isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
        } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none ${
          errors.description ? 'border-red-500' : ''
        }`}
      />
      <div className="flex items-center justify-between mt-2">
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {formData.description.length} caracteres
        </p>
        {errors.description && (
          <p className="text-red-500 text-sm flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.description}
          </p>
        )}
      </div>
    </div>

    {/* Comentarios adicionales */}
    <div>
      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'} flex items-center gap-2`}>
        <MessageSquare className="w-4 h-4" />
        Comentarios Adicionales (Opcional)
      </label>
      <textarea
        value={formData.additionalComments}
        onChange={(e) => updateField('additionalComments', e.target.value)}
        placeholder="Agrega cualquier información adicional que pueda ser útil..."
        rows={4}
        className={`w-full px-4 py-3 ${
          isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
        } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none`}
      />
    </div>
  </div>
);

  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          {t('tickets.wizard.fields.title')} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder={t('tickets.wizard.fields.titlePlaceholder')}
          className={`w-full px-4 py-3 ${
            isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
          } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
            errors.title ? 'border-red-500' : ''
          }`}
        />
        {errors.title && (
          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.title}
          </p>
        )}
      </div>

      {/* Category */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          {t('tickets.wizard.fields.category')} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            value={formData.category}
            onChange={(e) => updateField('category', e.target.value)}
            className={`w-full appearance-none px-4 py-3 pr-10 ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
            } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer ${
              errors.category ? 'border-red-500' : ''
            }`}
          >
            <option value="">{t('tickets.wizard.fields.selectCategory')}</option>
            <option value="diagnostic">{t('tickets.wizard.categories.diagnostic')}</option>
            <option value="maintenance">{t('tickets.wizard.categories.maintenance')}</option>
            <option value="repair">{t('tickets.wizard.categories.repair')}</option>
            <option value="consultation">{t('tickets.wizard.categories.consultation')}</option>
            <option value="other">{t('tickets.wizard.categories.other')}</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
        {errors.category && (
          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.category}
          </p>
        )}
      </div>

      {/* Priority */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          {t('tickets.wizard.fields.priority')} <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-4 gap-3">
          {['low', 'medium', 'high', 'critical'].map((priority) => (
            <button
              key={priority}
              type="button"
              onClick={() => updateField('priority', priority)}
              className={`px-4 py-3 rounded-lg font-medium text-sm transition-all border-2 ${
                formData.priority === priority
                  ? priority === 'low'
                    ? 'bg-gray-500/10 border-gray-500 text-gray-400'
                    : priority === 'medium'
                    ? 'bg-blue-500/10 border-blue-500 text-blue-400'
                    : priority === 'high'
                    ? 'bg-orange-500/10 border-orange-500 text-orange-400'
                    : 'bg-red-500/10 border-red-500 text-red-400'
                  : isDark
                  ? 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  : 'bg-gray-50 border-gray-300 text-gray-600 hover:border-gray-400'
              }`}
            >
              {t(`tickets.priority.${priority}`)}
            </button>
          ))}
        </div>
        {errors.priority && (
          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.priority}
          </p>
        )}
      </div>
    </div>
  );

const renderStep3 = () => {
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Validar tamaño (10MB por archivo)
    const invalidFiles = files.filter(f => f.size > 10 * 1024 * 1024);
    if (invalidFiles.length > 0) {
      showError('Algunos archivos exceden el tamaño máximo de 10MB');
      return;
    }
    
    // Validar cantidad total (máx 5)
    if (selectedFiles.length + files.length > 5) {
      showError('Máximo 5 archivos permitidos');
      return;
    }
    
    // Validar tipos permitidos
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    const invalidTypes = files.filter(f => !allowedTypes.includes(f.type));
    if (invalidTypes.length > 0) {
      showError('Solo se permiten imágenes (JPG, PNG, GIF) y PDF');
      return;
    }
    
    // Agregar archivos
    setSelectedFiles(prev => [...prev, ...files]);
    
    // Generar previews para imágenes
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(prev => ({
            ...prev,
            [file.name]: reader.result as string
          }));
        };
        reader.readAsDataURL(file);
      }
    });
  };
  
  const removeFile = (fileName: string) => {
    setSelectedFiles(prev => prev.filter(f => f.name !== fileName));
    setFilePreview(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[fileName];
      return newPreviews;
    });
  };

  return (
    <div className="space-y-6">
      <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border`}>
        <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'} flex items-center gap-2`}>
          <Sparkles className="w-4 h-4" />
          {t('tickets.wizard.preview.optional')} - La IA analizará esta información para dar mejores sugerencias
        </p>
      </div>

      {/* Campos técnicos existentes */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            {t('tickets.wizard.fields.motor')}
          </label>
          <input
            type="text"
            value={formData.motor}
            onChange={(e) => updateField('motor', e.target.value)}
            placeholder={t('tickets.wizard.fields.motorPlaceholder')}
            className={`w-full px-4 py-3 ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
            } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            {t('tickets.wizard.fields.configuration')}
          </label>
          <input
            type="text"
            value={formData.configuration}
            onChange={(e) => updateField('configuration', e.target.value)}
            placeholder={t('tickets.wizard.fields.configurationPlaceholder')}
            className={`w-full px-4 py-3 ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
            } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            {t('tickets.wizard.fields.component')}
          </label>
          <input
            type="text"
            value={formData.component}
            onChange={(e) => updateField('component', e.target.value)}
            placeholder={t('tickets.wizard.fields.componentPlaceholder')}
            className={`w-full px-4 py-3 ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
            } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            {t('tickets.wizard.fields.partNumber')}
          </label>
          <input
            type="text"
            value={formData.partNumber}
            onChange={(e) => updateField('partNumber', e.target.value)}
            placeholder={t('tickets.wizard.fields.partNumberPlaceholder')}
            className={`w-full px-4 py-3 ${
              isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
            } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
          />
        </div>
      </div>

      {/* 🖼️ ZONA DE UPLOAD DE IMÁGENES - NUEVO */}
      <div>
        <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'} flex items-center gap-2`}>
          <Image className="w-5 h-5" />
          Imágenes y Archivos (Opcional)
        </label>
        
        {/* Drop Zone */}
        <div className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          isDark 
            ? 'border-gray-700 hover:border-blue-500 bg-gray-900/50' 
            : 'border-gray-300 hover:border-blue-400 bg-gray-50'
        }`}>
          <input
            type="file"
            multiple
            accept="image/*,.pdf"
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          
          <Upload className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
            Click para seleccionar o arrastra archivos aquí
          </p>
          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            PNG, JPG, GIF o PDF (Máx. 10MB por archivo, hasta 5 archivos)
          </p>
        </div>

        {/* Preview de archivos seleccionados */}
        {selectedFiles.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {selectedFiles.length} archivo(s) seleccionado(s)
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className={`relative group ${
                    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  } border rounded-lg p-3`}
                >
                  {/* Preview imagen */}
                  {file.type.startsWith('image/') && filePreview[file.name] ? (
                    <img
                      src={filePreview[file.name]}
                      alt={file.name}
                      className="w-full h-24 object-cover rounded mb-2"
                    />
                  ) : (
                    <div className={`w-full h-24 flex items-center justify-center rounded mb-2 ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <Paperclip className={`w-8 h-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                  )}
                  
                  <p className={`text-xs font-medium truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {file.name}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  
                  {/* Botón eliminar */}
                  <button
                    type="button"
                    onClick={() => removeFile(file.name)}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sugerencias de IA */}
      {renderAISuggestions()}
    </div>
  );
};

const renderStep4 = () => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
        {t('tickets.wizard.preview.title')}
      </h3>
      <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
        {t('tickets.wizard.preview.subtitle')}
      </p>
    </div>

    <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-xl p-6 space-y-4`}>
      {/* Información Básica */}
      <div>
        <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
          {t('tickets.wizard.preview.basicInfo')}
        </h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>{t('tickets.wizard.fields.title')}:</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{formData.title}</span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>{t('tickets.wizard.fields.category')}:</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t(`tickets.wizard.categories.${formData.category}`)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>{t('tickets.wizard.fields.priority')}:</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t(`tickets.priority.${formData.priority}`)}
            </span>
          </div>
        </div>
      </div>

      {/* Descripción */}
      <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} pt-4`}>
        <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
          {t('tickets.wizard.fields.description')}:
        </h4>
        <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap`}>
          {formData.description}
        </p>
      </div>

      {/* Información Técnica */}
      {(formData.motor || formData.configuration || formData.component || formData.partNumber) && (
        <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} pt-4`}>
          <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
            {t('tickets.wizard.preview.technicalInfo')}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {formData.motor && (
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>Motor:</span>
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{formData.motor}</span>
              </div>
            )}
            {formData.configuration && (
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>{t('tickets.wizard.fields.configuration')}:</span>
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{formData.configuration}</span>
              </div>
            )}
            {formData.component && (
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>{t('tickets.wizard.fields.component')}:</span>
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{formData.component}</span>
              </div>
            )}
            {formData.partNumber && (
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>{t('tickets.wizard.fields.partNumber')}:</span>
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{formData.partNumber}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 🖼️ IMÁGENES ADJUNTAS - NUEVO */}
      {selectedFiles.length > 0 && (
        <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} pt-4`}>
          <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-3 flex items-center gap-2`}>
            <Image className="w-4 h-4" />
            Archivos Adjuntos ({selectedFiles.length})
          </h4>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className={`${
                  isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                } border rounded-lg p-2 group relative`}
              >
                {/* Preview de imagen */}
                {file.type.startsWith('image/') && filePreview[file.name] ? (
                  <img
                    src={filePreview[file.name]}
                    alt={file.name}
                    className="w-full h-20 object-cover rounded mb-2"
                  />
                ) : (
                  <div className={`w-full h-20 flex items-center justify-center rounded mb-2 ${
                    isDark ? 'bg-gray-600' : 'bg-gray-200'
                  }`}>
                    <Paperclip className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                )}
                
                {/* Nombre del archivo */}
                <p className={`text-xs font-medium truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {file.name}
                </p>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {(file.size / 1024).toFixed(1)} KB
                </p>

                {/* Badge de tipo */}
                <div className="absolute top-1 right-1">
                  {file.type.startsWith('image/') ? (
                    <div className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                      IMG
                    </div>
                  ) : (
                    <div className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                      PDF
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comentarios adicionales */}
      {formData.additionalComments && (
        <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} pt-4`}>
          <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
            Comentarios Adicionales:
          </h4>
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap text-sm`}>
            {formData.additionalComments}
          </p>
        </div>
      )}
    </div>
  </div>
);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            type={toast.type}
            message={toast.message}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard/tickets')}
            className={`flex items-center gap-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-4 transition-colors group`}
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            {t('tickets.detail.backToTickets')}
          </button>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('tickets.wizard.title')}
          </h1>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Content */}
        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-xl p-8 mb-6`}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard/tickets')}
            className={`px-6 py-3 ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded-lg font-medium transition-all flex items-center gap-2`}
          >
            <X className="w-5 h-5" />
            {t('tickets.wizard.actions.cancel')}
          </button>

          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                className={`px-6 py-3 ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} rounded-lg font-medium transition-all flex items-center gap-2`}
              >
                <ArrowLeft className="w-5 h-5" />
                {t('tickets.wizard.actions.previous')}
              </button>
            )}

            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all flex items-center gap-2 shadow-lg shadow-blue-500/30"
              >
                {t('tickets.wizard.actions.next')}
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all flex items-center gap-2 shadow-lg shadow-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-5 h-5" />
                {isSubmitting ? t('tickets.wizard.actions.submitting') : t('tickets.wizard.actions.submit')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}